/*
  Состояние версии для слабовидящих на странице должника: включение, выбор,
  запоминание и чтение вслух (что бывает и как хранится - `lowVision.js`).

  🔴 Атрибуты ставятся на <html>, а не на страницу: цвет фона и размер шрифта
  должны дойти до самого верха документа, иначе по краям остаются светлые поля
  обычной версии. Поэтому уход со страницы ОБЯЗАН их снимать - кабинет
  сотрудника открывается в той же вкладке и иначе унаследовал бы чужой режим.
*/

import { useCallback, useEffect, useRef, useState } from 'react'

import { ATTRIBUTE_NAMES, attributesFor, readSettings, writeSettings } from './lowVision.js'

// Что читается вслух по нажатию: строки, заголовки, пояснения.
const READABLE = '.head__title, .head__note, .section__title, tr, .log__row, .muted, .notice, .qr__hint'
// Нажатие на то, что само что-то делает, не читается - оно делает своё.
const ACTIVE = 'button, a, input, textarea, select, label'
// Строка таблицы читается по ячейкам через запятую, а не слитно в одно слово.
const ROW = 'tr, .log__row'

function browserStorage() {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function speechAvailable() {
  return Boolean(window.speechSynthesis) && typeof window.SpeechSynthesisUtterance === 'function'
}

function spokenText(block) {
  const parts = block.matches(ROW)
    ? [...block.children].map((cell) => cell.textContent.trim()).filter(Boolean)
    : [block.textContent.trim()]
  // Знак рубля голос читает по-разному или не читает вовсе. Длинный номер
  // (расчётный счёт, ИНН, БИК) - по цифрам: целиком голос читает его как
  // число в квинтиллионах, и переписать его на слух нельзя. Суммы не задеты:
  // в них цифры разбиты по три.
  return parts
    .join(', ')
    .replace(/₽/g, 'рублей')
    .replace(/\d{7,}/g, (number) => number.split('').join(' '))
}

export default function useLowVision() {
  const [state, setState] = useState(() => readSettings(browserStorage()))
  const [canSpeak] = useState(speechAvailable)
  const reading = useRef(null)

  // Голос проверяется в момент вызова, а не берётся из `canSpeak`: остановка
  // идёт и при уходе со страницы, когда окружение уже может быть другим.
  const stopReading = useCallback(() => {
    window.speechSynthesis?.cancel()
    reading.current?.classList.remove('lv-reading')
    reading.current = null
  }, [])

  useEffect(() => {
    const root = document.documentElement
    for (const [name, value] of Object.entries(attributesFor(state))) {
      root.setAttribute(name, value)
    }
    writeSettings(browserStorage(), state)
    return () => {
      for (const name of ATTRIBUTE_NAMES) root.removeAttribute(name)
    }
  }, [state])

  useEffect(() => {
    if (!state.on || state.speech !== 'on') stopReading()
  }, [state.on, state.speech, stopReading])

  useEffect(() => stopReading, [stopReading])

  const readAloud = useCallback(
    (event) => {
      if (!state.on || state.speech !== 'on' || !canSpeak) return
      if (event.target.closest(ACTIVE)) return
      const block = event.target.closest(READABLE)
      if (!block) return
      stopReading()
      const phrase = new window.SpeechSynthesisUtterance(spokenText(block))
      phrase.lang = 'ru-RU'
      phrase.rate = 0.9
      phrase.onend = () => block.classList.remove('lv-reading')
      block.classList.add('lv-reading')
      reading.current = block
      window.speechSynthesis.speak(phrase)
    },
    [state.on, state.speech, canSpeak, stopReading],
  )

  return {
    state,
    canSpeak,
    turnOn: () => setState((current) => ({ ...current, on: true })),
    turnOff: () => setState((current) => ({ ...current, on: false })),
    choose: (name, value) => setState((current) => ({ ...current, [name]: value })),
    readAloud,
  }
}
